/**
 * Stateless debounced autosave hook for clarifying answers.
 *
 * Callers MUST pass a `useCallback`-wrapped `getAnswers` function so that
 * this hook always reads the latest answers without stale-closure issues.
 * This hook owns no answer state — the consumer owns answer state and calls
 * `scheduleSave` after each change.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useAutoSaveCoordinationOptional } from '@/components/proposals/shared/useAutoSaveCoordination';

import type { ClarifyingAnswer } from '../core/types';
import { useUpdateClarifyingAnswers } from '../query/mutations';

// ── Module-scope constants ──────────────────────────────────────────────────

// Intentionally separate from the identically-valued constant in
// AskUserForInfoRenderer.tsx — each system can be tuned independently.
const AUTOSAVE_DEBOUNCE_MS = 2000;

/**
 * The coordination registration id this hook uses, exported so callers that
 * need to exclude this hook's own registration from a flush (e.g.
 * `useClarifyingAnswers`'s `submitAnswers`, which already flushes and stamps
 * this hook's data itself via `submit()`) can reference it without
 * duplicating the id format.
 */
export function getClarifyingAnswersRegistrationId(proposalId: string): string {
  return `clarifying-answers-${proposalId}`;
}

// ── Types ────────────────────────────────────────────────────────────────────

type CoordinationHandle = ReturnType<
  NonNullable<ReturnType<typeof useAutoSaveCoordinationOptional>>['register']
>;

export interface UseClarifyingAnswersAutoSaveParams {
  orgId: string;
  proposalId: string;
  /**
   * Returns the current answers. MUST be wrapped in `useCallback` by the caller
   * so this hook always reads the latest answers without becoming stale.
   * This hook owns no answer state — the consumer owns it.
   */
  getAnswers: () => ClarifyingAnswer[];
  editable: boolean;
}

export interface UseClarifyingAnswersAutoSaveResult {
  scheduleSave: () => void;
  flush: () => Promise<void> | void;
  /**
   * Saves immediately with `submitted: true` so the backend stamps
   * answers_submitted_at. Unlike flush(), this always issues a save (even with
   * no pending debounce) — the stamp itself is the point.
   */
  submit: () => Promise<void>;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  cancelPendingSave: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useClarifyingAnswersAutoSave({
  orgId,
  proposalId,
  getAnswers,
  editable,
}: UseClarifyingAnswersAutoSaveParams): UseClarifyingAnswersAutoSaveResult {
  const [saveState, setSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');

  // Keep refs up-to-date on every render so closures always read the latest
  // values without capturing stale props.
  const getAnswersRef = useRef(getAnswers);
  getAnswersRef.current = getAnswers;

  const editableRef = useRef(editable);
  editableRef.current = editable;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Points at the active coordination registration so save can report
  // state changes without re-registering on every save identity change.
  const coordinationHandleRef = useRef<CoordinationHandle | null>(null);

  // Always points at the latest save logic so the debounce timer and the
  // registered flush always invoke the current save without re-arming.
  const saveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Tracks the currently in-flight save so a concurrent call (e.g. flush()
  // firing while the debounced save is still running) reuses that promise
  // instead of starting a second, overlapping mutateAsync call.
  const inFlightSaveRef = useRef<Promise<void> | null>(null);

  // When runSave() is called while a save is in-flight, this flag requests a
  // coalesced re-run once the current save settles. The re-run reads the latest
  // answers via getAnswersRef so no edit is ever silently dropped.
  const pendingReRunRef = useRef(false);

  // When submit() is called, the next save carries submitted: true (stamping
  // answers_submitted_at server-side). A ref — not a parameter — so the flag
  // survives runSave's coalescing: a submit issued while an autosave is
  // in-flight is honoured by the coalesced re-run. Cleared at the start of
  // each save attempt that carries it (a failed submit is retried by the user
  // pressing Submit again, which re-sets it).
  const submitRequestedRef = useRef(false);

  // Stable pointer to runSave used inside the .finally() re-run callback to
  // avoid a self-referential useCallback (which ESLint flags as circular).
  const runSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // All hooks before any conditional returns.
  const coordination = useAutoSaveCoordinationOptional();
  const { mutateAsync } = useUpdateClarifyingAnswers(Number(orgId));

  // ── Save callback ─────────────────────────────────────────────────────────

  const save = useCallback(async () => {
    if (!editableRef.current) {
      return;
    }
    if (coordination?.isAutoSaveFrozen()) {
      setSaveState('idle');
      coordinationHandleRef.current?.setSaveState('idle');
      return;
    }
    setSaveState('saving');
    coordinationHandleRef.current?.setSaveState('saving');
    const submitted = submitRequestedRef.current;
    submitRequestedRef.current = false;
    try {
      await mutateAsync({
        proposalId: Number(proposalId),
        answers: getAnswersRef.current(),
        submitted,
      });
      setSaveState('saved');
      coordinationHandleRef.current?.setSaveState('idle');
    } catch (err) {
      setSaveState('error');
      coordinationHandleRef.current?.setSaveState('error');
      throw err;
    }
  }, [coordination, mutateAsync, proposalId]);
  // Update saveRef every render so debounce timer and coordination flush always
  // call the latest save without needing to re-register.
  saveRef.current = save;

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Runs the current save. If one is already in-flight, marks a coalesced
   * re-run (pendingReRunRef) and returns the existing promise. When the
   * in-flight save settles its .finally() fires the re-run, and the original
   * promise chains to that re-run's outcome — so a caller awaiting the
   * returned promise sees the coalesced re-run's real success/failure, not
   * just the first attempt's result.
   */
  const runSave = useCallback((): Promise<void> => {
    if (inFlightSaveRef.current) {
      pendingReRunRef.current = true;
      return inFlightSaveRef.current;
    }
    const savePromise = saveRef.current().finally(() => {
      inFlightSaveRef.current = null;
      if (pendingReRunRef.current) {
        pendingReRunRef.current = false;
        // Return the re-run's promise from .finally() so the outer promise
        // chains to it: if the re-run rejects, the original promise rejects
        // too, even if the first attempt succeeded.
        return runSaveRef.current();
      }
    });
    inFlightSaveRef.current = savePromise;
    return savePromise;
  }, []);
  runSaveRef.current = runSave;

  /**
   * Arms a debounced save. No-ops when the component is not editable.
   * Call after each answer change.
   */
  const scheduleSave = useCallback(() => {
    if (!editableRef.current) {
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Mark the registration pending as soon as the debounce is armed so the
    // coordination aggregate reports `hasPendingOrSaving`. Without this the
    // approve-button flush rail (gated on that aggregate) skips this component
    // and the user's latest debounced answers are dropped on approve.
    setSaveState('saving');
    coordinationHandleRef.current?.setSaveState('pending');
    debounceTimerRef.current = setTimeout(() => {
      void runSave();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [runSave]);

  /**
   * Cancels any pending debounce and saves immediately. If a save is already
   * in flight, awaits that save instead of firing a second concurrent one.
   * Returns the mutation promise so callers can await completion.
   */
  const flush = useCallback((): Promise<void> => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    return runSave();
  }, [runSave]);

  /**
   * Saves immediately with `submitted: true` so the backend stamps
   * answers_submitted_at. When an autosave is in-flight, runSave's coalescing
   * re-runs after it settles and the re-run picks up the submit flag.
   */
  const submit = useCallback((): Promise<void> => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    submitRequestedRef.current = true;
    if (inFlightSaveRef.current) {
      // Force the coalesced re-run so the submit flag is actually sent even
      // when the in-flight autosave already covers the latest answers.
      pendingReRunRef.current = true;
      return inFlightSaveRef.current;
    }
    return runSave();
  }, [runSave]);

  /**
   * Cancels the pending debounce timer without triggering a save. Call this
   * before resetting answer state (e.g. on question-set change) to prevent a
   * stale save from firing against the newly seeded answers.
   */
  const cancelPendingSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // ── AutoSaveCoordination registration ─────────────────────────────────────
  // Register with the coordination provider (if mounted) so the approve-button
  // flush rail can flush pending edits before proceeding. The saveRef pointer
  // keeps the registered flush pointed at the latest save logic without
  // re-registering whenever save's identity changes.

  useEffect(() => {
    if (!coordination) {
      return;
    }
    const handle = coordination.register(
      getClarifyingAnswersRegistrationId(proposalId)
    );
    coordinationHandleRef.current = handle;

    handle.setFlushFn(async () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      await runSave();
    });

    return () => {
      handle.unregister();
      coordinationHandleRef.current = null;
    };
  }, [coordination, proposalId, runSave]);

  // ── Cleanup ───────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { scheduleSave, flush, submit, saveState, cancelPendingSave };
}
